import range from 'lodash/range';
import { assert } from '../../utils/assert';
import { executeSoon } from '../../utils/executeSoon';
import { formBroadcastUrl } from '../../utils/url';
import { BroadcastService } from '../broadcast/BroadcastService';
import { ScheduleService } from '../schedule/ScheduleService';
import { RecordingServiceOutput } from './RecordingServiceOutput';
const RECORDER_START_URL = import.meta.env.RECORDER_START_URL;
const RECORDER_STOP_URL = import.meta.env.RECORDER_STOP_URL;
const RECORDER_STORAGE_URL = import.meta.env.RECORDER_STORAGE_URL;
const THUMBNAIL_SUFFIX = '.thumb.png';
// RECORDINGS_LIST_LIMIT % LIST_PARALLELISM should be 0 to avoid duplicated entries
const RECORDINGS_LIST_LIMIT = 15;
const LIST_PARALLELISM = 3;
const HEARTBEAT_TIMEOUT_MS = 5000;
/**
 * Implementation of API calls based on Henri's demo - https://github.com/streamr-dev/streamrtv-recorder/blob/main/bin/recording-utils.ts
 * His comments were also copied here
 */
export class RecordingService {
    static instance;
    recordingStartAbortController;
    recordingsListAbortController;
    activeRecording;
    recordingsList;
    heartbeatTimeoutId;
    static getInstance() {
        if (!RecordingService.instance) {
            RecordingService.instance = new RecordingService();
        }
        return RecordingService.instance;
    }
    static getNotificationInterface() {
        return RecordingService.getInstance();
    }
    static getOutput() {
        return RecordingServiceOutput.getReactiveOutput();
    }
    output;
    constructor() {
        this.output = new RecordingServiceOutput();
        BroadcastService.getEmitterInterface().on('heartbeatFromRecorder', () => this.onHeartbeatFromRecorder());
    }
    async init() {
        // No initialization needed
    }
    notifyStartRecording(broadcast, visibility) {
        let broadcastUrlForRecorder = formBroadcastUrl(broadcast);
        const devRecorderGatewayUrl = import.meta.env.DEV_RECORDER_GATEWAY_URL;
        if (devRecorderGatewayUrl) {
            const { protocol, hostname, port } = new URL(devRecorderGatewayUrl);
            broadcastUrlForRecorder = Object.assign(new URL(broadcastUrlForRecorder), {
                protocol,
                hostname,
                port,
            }).toString();
        }
        this.output.setActiveRecording({
            visibility: visibility,
            initializedAt: Date.now(),
            status: 'initializing',
        });
        this.activeRecording = {
            broadcast: {
                streamId: broadcast.streamId,
                broadcasterUserId: broadcast.broadcasterUserId,
                aesKey: broadcast.aesKey,
            },
            visibility,
            status: 'initializing',
        };
        executeSoon(() => this.initializeActiveRecording());
    }
    notifyStopRecording(doneCallback) {
        if (this.heartbeatTimeoutId) {
            window.clearTimeout(this.heartbeatTimeoutId);
            this.heartbeatTimeoutId = undefined;
        }
        const sessionId = this.activeRecording?.sessionId;
        if (sessionId) {
            assert(this.activeRecording, 'Recording is not active');
            this.output.setActiveRecordingStatus('stopping');
            this.activeRecording.status = 'stopping';
            executeSoon(async () => await this.stopRecording(sessionId), undefined, () => {
                this.recordingStartAbortController?.abort();
                this.output.setActiveRecording(undefined);
                this.activeRecording = undefined;
                doneCallback?.();
            });
        }
        else {
            this.recordingStartAbortController?.abort();
            this.output.setActiveRecording(undefined);
            this.activeRecording = undefined;
            doneCallback?.();
        }
    }
    notifyRetryInitialization() {
        assert(this.activeRecording, 'Recording is not active');
        this.output.setActiveRecordingInitializedAt(Date.now());
        this.output.setActiveRecordingStatus('initializing');
        this.activeRecording.status = 'initializing';
        executeSoon(() => this.initializeActiveRecording());
    }
    notifyFetchRecordingsList(broadcasterUserId, streamId, includePrivate) {
        this.recordingsList = {
            broadcast: {
                streamId,
                broadcasterUserId,
            },
            includePrivate,
            offset: 0,
        };
        this.output.initRecordingsList();
        executeSoon(() => this.listRecordings());
    }
    notifyFetchMoreRecordings() {
        executeSoon(() => this.listRecordings());
    }
    notifyResetRecordingsList() {
        this.recordingsListAbortController?.abort();
        this.recordingsList = undefined;
        this.output.clearRecordingsList();
    }
    onHeartbeatFromRecorder() {
        if (this.activeRecording) {
            if (this.activeRecording.status !== 'stopping') {
                this.output.setActiveRecordingStatus('recording');
                this.activeRecording.status = 'recording';
            }
            if (this.heartbeatTimeoutId) {
                window.clearTimeout(this.heartbeatTimeoutId);
            }
            this.heartbeatTimeoutId = window.setTimeout(() => {
                if (this.activeRecording) {
                    this.output.setActiveRecordingStatus('error');
                    this.activeRecording.status = 'error';
                }
                this.heartbeatTimeoutId = undefined;
            }, HEARTBEAT_TIMEOUT_MS);
        }
    }
    async initializeActiveRecording() {
        assert(this.activeRecording, 'Recording is not active');
        this.recordingStartAbortController?.abort();
        this.recordingStartAbortController = new AbortController();
        try {
            const response = await fetch(RECORDER_START_URL, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    broadcast: this.activeRecording.broadcast,
                    visibility: this.activeRecording.visibility,
                }),
            });
            if (!response.ok) {
                throw new Error(`Failed to start recording: ${response.statusText}`);
            }
            const data = (await response.json());
            this.activeRecording.sessionId = data.sessionId;
        }
        catch (error) {
            if (error instanceof Error && error.name === 'AbortError') {
                // Ignore aborts
            }
            else {
                console.error('Error starting recording:', error);
                this.output.setActiveRecordingStatus('error');
                this.activeRecording.status = 'error';
            }
        }
        this.recordingStartAbortController = undefined;
    }
    async stopRecording(sessionId) {
        const response = await fetch(RECORDER_STOP_URL, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ sessionId }),
        });
        if (!response.ok) {
            throw new Error('Failed to stop recording');
        }
        const data = (await response.json());
        if (this.activeRecording?.visibility === 'private') {
            const scheduledBroadcast = ScheduleService.getInstance().getRequiredScheduledBroadcast(this.activeRecording.broadcast.streamId);
            scheduledBroadcast.privateRecordingUrls = [
                ...(scheduledBroadcast.privateRecordingUrls ?? []),
                data.storageUrl,
            ];
            ScheduleService.getInstance().notifyUpdateScheduledBroadcast(scheduledBroadcast);
        }
    }
    async listRecordings() {
        assert(this.recordingsList, 'Active recordings list is not set');
        this.recordingsListAbortController?.abort();
        this.recordingsListAbortController = new AbortController();
        const { signal } = this.recordingsListAbortController;
        if (this.recordingsList.offset > 0) {
            this.output.updateRecordingsListStatus('loadingMore');
        }
        try {
            const publicRecordings = await this.listPublicRecordings(this.recordingsList.broadcast, RECORDINGS_LIST_LIMIT, this.recordingsList.offset, signal);
            const privateRecordings = this.recordingsList.includePrivate
                ? await this.listPrivateRecordings(this.recordingsList.broadcast.streamId, RECORDINGS_LIST_LIMIT, this.recordingsList.offset, signal)
                : [];
            // If aborted during requests, do not update state
            if (signal.aborted) {
                return;
            }
            const hasMore = publicRecordings.length === RECORDINGS_LIST_LIMIT ||
                privateRecordings.length === RECORDINGS_LIST_LIMIT;
            const recordings = [
                ...publicRecordings,
                ...privateRecordings,
            ].filter((recording) => recording.isReady);
            recordings.sort((a, b) => b.timestamp - a.timestamp);
            this.output.updateRecordingsListEntries(recordings);
            this.output.updateRecordingsListHasMore(hasMore);
            this.output.updateRecordingsListStatus('loaded');
            this.recordingsList.offset += RECORDINGS_LIST_LIMIT;
        }
        catch (error) {
            if (error instanceof Error && error.name === 'AbortError') {
                // Ignore aborts
            }
            else {
                console.error('Error fetching recordings list:', error);
                this.output.updateRecordingsListStatus('error');
                this.recordingsList = undefined;
            }
        }
        finally {
            this.recordingsListAbortController = undefined;
        }
    }
    async listPublicRecordings(broadcast, limit, offset = 0, signal) {
        /*
        Eventually, recordings can be listed using an API endpoint on the StreamrTV backend, but that's not available yet. For the MVP, let's use the fact that public recordings have a predictable filename (and thumbnail filename). Basically iterate the deterministic filenames until there's no longer a match:
        Try with a HTTP HEAD request whether https://streamrtv-recorder-files.s3.amazonaws.com/(streamId)/public/0001.mp4 exists -> you get response code 200 if it does
        If the first one was successful, then try if https://streamrtv-recorder-files.s3.amazonaws.com/(streamId)/public/0002.mp4 exists, etc., always incrementing counter by one
        Repeat until the response code is not 200
        */
        // Start with the first index, noting offset
        let counter = 1 + offset;
        const recordings = [];
        // Make parallel requests until one rejects
        while (true) {
            if (signal?.aborted) {
                break;
            }
            // Create an array of promises for concurrent requests
            const promises = range(LIST_PARALLELISM).map((i) => {
                const url = this.getPublicRecordingUrl(broadcast.broadcasterUserId, broadcast.streamId, counter + i);
                return this.getRecording(url, signal).catch(() => null); // Return null if request fails
            });
            // Wait for all requests to complete
            const results = await Promise.all(promises);
            // Check if any requests succeeded
            const successfulResults = results.filter((result) => result !== null);
            if (successfulResults.length === 0) {
                // No more recordings found, break the loop
                break;
            }
            // Add successful recordings and continue
            recordings.push(...successfulResults);
            counter += LIST_PARALLELISM;
            // Check if we've reached the limit
            if (recordings.length >= limit) {
                break;
            }
        }
        // Sort the recordings array by timestamp in descending order and apply limit
        return recordings
            .sort((a, b) => b.timestamp - a.timestamp)
            .slice(0, limit);
    }
    async listPrivateRecordings(streamId, limit, offset = 0, signal) {
        const scheduledBroadcast = ScheduleService.getInstance().getRequiredScheduledBroadcast(streamId);
        const privateRecordings = scheduledBroadcast.privateRecordingUrls ?? [];
        const selectedRecordings = privateRecordings.slice(offset, offset + limit);
        if (selectedRecordings.length === 0) {
            return [];
        }
        const results = await Promise.all(selectedRecordings.map((recordingUrl) => this.getRecording(recordingUrl, signal).catch(() => null)));
        return results.filter((result) => result !== null);
    }
    async getRecording(url, signal) {
        // Use the HEAD request to check if the recording exists - don't download the potentially large file!
        const response = await fetch(url, { method: 'HEAD', signal });
        // If the response is a 200, return the recording
        if (response.status === 200) {
            return this.responseToRecording(url, response);
        }
        // If the response is an error message, throw an error
        throw new Error(`Recording not found: ${url}`);
    }
    responseToRecording(url, response) {
        const bytes = Number.parseInt(response.headers.get('content-length') ?? '0');
        return {
            url,
            thumbnailUrl: `${url}${THUMBNAIL_SUFFIX}`,
            timestamp: new Date(response.headers.get('last-modified') ?? '').getTime(),
            contentLength: bytes,
            isReady: bytes > 0,
            isPublic: this.isDiscoverable(url),
        };
    }
    isDiscoverable(url) {
        return url.includes('/public/');
    }
    getPublicRecordingUrl(broadcasterUserId, streamId, counter) {
        return `${RECORDER_STORAGE_URL}/${broadcasterUserId.toLowerCase()}/${streamId}/recordings/public/${counter}.mp4`;
    }
}
